import { useEscapeLayer } from '@ui';
import { useRef, useState, type PointerEvent } from 'react';
import { CropIcon } from './icons';
import { BUG_OWN_ATTR } from './recorder';

export interface AreaRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const MIN_SIDE = 8;

export function AreaPicker({ onPick, onCancel }: { onPick: (rect: AreaRect) => void; onCancel: () => void }) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const [rect, setRect] = useState<AreaRect | null>(null);

  // Esc cancels the frame when it is the topmost layer (facts model widget_ui, П306).
  useEscapeLayer(true, onCancel);

  const box = (event: PointerEvent<HTMLDivElement>): AreaRect | null => {
    if (!start.current) return null;
    const x = Math.max(0, Math.min(start.current.x, event.clientX));
    const y = Math.max(0, Math.min(start.current.y, event.clientY));
    const w = Math.min(window.innerWidth, Math.max(start.current.x, event.clientX)) - x;
    const h = Math.min(window.innerHeight, Math.max(start.current.y, event.clientY)) - y;
    return { x, y, w, h };
  };

  return (
    <div
      className={`bug-area${rect ? ' bug-area-on' : ''}`}
      {...{ [BUG_OWN_ATTR]: '' }}
      role="application"
      aria-label="Снимок области"
      onContextMenu={(event) => {
        event.preventDefault();
        onCancel();
      }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        start.current = { x: event.clientX, y: event.clientY };
        setRect({ x: event.clientX, y: event.clientY, w: 0, h: 0 });
      }}
      onPointerMove={(event) => {
        const next = box(event);
        if (next) setRect(next);
      }}
      onPointerUp={(event) => {
        const next = box(event);
        start.current = null;
        if (next && next.w >= MIN_SIDE && next.h >= MIN_SIDE) onPick(next);
        else onCancel();
      }}
      onPointerCancel={onCancel}
    >
      {rect ? (
        <div className="bug-area-rect" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}>
          {rect.w >= MIN_SIDE && rect.h >= MIN_SIDE && (
            <span className="bug-area-size">
              {Math.round(rect.w)}×{Math.round(rect.h)}
            </span>
          )}
        </div>
      ) : (
        <span className="bug-area-hint">
          <CropIcon /> <kbd>Esc</kbd>
        </span>
      )}
    </div>
  );
}
